import React from 'react'
import { useLocation } from 'react-router-dom'

import getPhoenixUrlParams from '../lib/get-phoenix-url-params'
import mapCheckoutDataToCheckoutStepsFlags from '../lib/map-checkout-data-to-checkout-steps-flags'
import Cart from '../typings/Cart'

import { OrderCreationHandlerCartDocument, OrderCreationHandlerCartQuery } from '../graphql'
import CreateOrderFromCartHandler from './CreateOrderFromCartHandler'

import LoadingPage from './LoadingPage'
import useCart from './useCart'

const checkIfCartRequiresUserInteraction = (cart: Cart) => {
  const { hasProductToSelect, hasAdditionalInformation, isQuickCheckoutAvailable, hasBufferPage } =
    mapCheckoutDataToCheckoutStepsFlags(cart)

  if (!hasAdditionalInformation && isQuickCheckoutAvailable) {
    return false
  }

  if (hasProductToSelect || hasAdditionalInformation || hasBufferPage) {
    return true
  }

  return false
}

const OrderCreationHandler: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const location = useLocation()
  const { cartId, orderId } = getPhoenixUrlParams(location.search)

  if (location.pathname !== '/' || orderId) {
    return <>{children}</>
  }

  if (!cartId) {
    throw new Error('A cart id should already exist at this point')
  }

  // here we actually are calling the react hook conditionally only because
  // in the case of the above conditions, the hook itself will never be called
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { cartData, isCartLoading } = useCart<OrderCreationHandlerCartQuery>(OrderCreationHandlerCartDocument, {
    nextFetchPolicy: 'standby',
  })

  if (!cartData || isCartLoading) {
    return <LoadingPage />
  }

  const cartRequiresUserInteraction = checkIfCartRequiresUserInteraction(cartData.cart)

  if (cartRequiresUserInteraction) {
    return <>{children}</>
  }

  return <CreateOrderFromCartHandler skipConfirmation={cartData.cart.quickCheckoutAvailable} />
}

export default OrderCreationHandler
